import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { collection, query, where, orderBy, getDocs, type Timestamp } from 'firebase/firestore'
import { RefreshCw, MessageSquareX, Eye } from 'lucide-react'
import { Layout } from '../components/layout/Layout'
import { MasterclassTicketModal } from '../components/masterclass/MasterclassTicketModal'
import { ManagedSeo } from '../components/seo/ManagedSeo'

import { assets } from '../config/assets'
import { db } from '../lib/firestore'
import { logGaEvent } from '../lib/analytics'
import { buildClinicEventSchemas, buildFaqSchema, AH_ORGANIZATION, SCOTT_PERSON } from '../config/schema'

// ─── TYPES ───────────────────────────────────────────────────────────────────

interface MasterclassEvent {
  id: string
  eventName: string
  location: string
  venueName: string
  venueAddress: string
  startDate: Date
  endDate: Date
  durationDays?: 2 | 3
  riderTicketsSold: number
  /** Arena capacity, adjustable per event from the dashboard. */
  riderCapacity: number
  riderSoldOut: boolean
  active: boolean
}

// ─── DATA ────────────────────────────────────────────────────────────────────

const CREDENTIALS = [
  {
    stat: '20+ Years',
    body: 'Working professionally with horses and riders across disciplines',
  },
  {
    stat: 'Horse Behaviorist',
    body: 'Trained to identify and address the root cause of behavior, not just the symptom',
  },
  {
    stat: '1,000+ Riders',
    body: 'Coached riders at every level, from first-time horse owners to seasoned competitors',
  },
]

const TESTIMONIALS = [
  {
    quote:
      'I hauled my mare Penny six hours from Kansas City because a friend told me Scott would find things nobody else could. Day one he spotted a brace in my left hip I had been compensating for since a car accident. Penny stopped drifting left by day two.',
    name: 'Carla Weaver',
    location: 'Kansas City, Missouri',
  },
  {
    quote:
      'I brought my 5-year-old gelding who had been rushing fences. Scott spent the first hour on the ground and never touched a jump. By the afternoon my horse was waiting for me instead of running through me.',
    name: 'Sue Parrish',
    location: 'Richmond, Virginia',
  },
  {
    quote:
      'Scott told me on day one that my horse was not spooky, I was just late with my aids. That one sentence rewired how I ride. I stopped trying to prevent spooks and started giving Duke something to focus on.',
    name: 'Amy Kline',
    location: 'Spokane, Washington',
  },
]

const FAQS: { q: string; a: string }[] = [
  {
    q: 'Do I need to bring my own horse?',
    a: 'Yes. This is a working event. You and your horse do the work directly with Scott. If you do not have a horse to bring, you are welcome to attend as an auditor.',
  },
  {
    q: 'What experience level is required?',
    a: 'All levels are welcome. Scott creates a specific program for each rider individually, so the weekend works whether you are relatively new or have been riding for decades.',
  },
  {
    q: 'Can I bring a green horse or one with behavioral issues?',
    a: 'Yes, with one exception. You can bring a green horse or one with behavioral issues. You cannot bring a horse that has never been started and expect Scott to start it at this event. An unstarted horse can attend for groundwork and exposure only, unless Scott has specified otherwise in advance.',
  },
  {
    q: 'What disciplines are welcome?',
    a: 'All disciplines. Western, English, trail, liberty, groundwork. The principles apply across every discipline.',
  },
  {
    q: 'Is there a minimum age requirement?',
    a: 'Participants must be 12 years of age or older.',
  },
  {
    q: 'Does my horse need a Coggins or health certificate?',
    a: 'A current negative Coggins is required. If you are crossing state lines, a health certificate is required by state law. Vaccine requirements vary by venue. Contact Allie at allie@advantagehorsemanship.com with any questions before purchasing.',
  },
  {
    q: 'Can I board my horse at the venue?',
    a: 'Stabling varies by location. Contact Allie at allie@advantagehorsemanship.com for stabling information specific to the event you are attending.',
  },
  {
    q: 'Are meals provided?',
    a: 'Lunch on Friday is shared as a group. Other meals are not provided, but food is generally available nearby. Details are sent to registered participants ahead of each event.',
  },
  {
    q: 'What is the cancellation and refund policy?',
    a: 'Cancellations more than two weeks before the event start date receive a refund minus a $200 non-refundable deposit. Cancellations within two weeks are not eligible for a refund. Email allie@advantagehorsemanship.com to request a cancellation.',
  },
  {
    q: 'Is a payment plan available?',
    a: 'Yes, on 3-day Masterclasses. A 3-month payment plan of $283 per month, $849 total, is offered at checkout for events more than 105 days away. Select it when you register. The payment plan is not available on 2-day Masterclasses or on events inside the 105-day window.',
  },
  {
    q: 'Can auditors ask questions during sessions?',
    a: 'Yes. Auditors are welcome to ask questions throughout the weekend.',
  },
]

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatDateRange(start: Date, end: Date): string {
  const opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' }
  const s = start.toLocaleDateString('en-US', opts)
  const e = end.toLocaleDateString('en-US', yearOpts)
  return `${s} – ${e}`
}

// Uses local-time setDate/getDate to match formatDateRange's local-time output.
// (See SchedulePage.tsx for the same helper.)
function getEventDayLabels(startDate: Date, durationDays: number): string[] {
  const labels: string[] = []
  for (let i = 0; i < durationDays; i++) {
    const d = new Date(startDate)
    d.setDate(startDate.getDate() + i)
    labels.push(d.toLocaleDateString('en-US', { weekday: 'long' }))
  }
  return labels
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="flex-shrink-0 mt-0.5">
      <circle cx="10" cy="10" r="9" stroke="#C8A84B" strokeWidth="1.5" />
      <path d="M6.5 10l2.5 2.5 4.5-5" stroke="#C8A84B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function SmallCheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="flex-shrink-0">
      <path d="M3 8l3.5 3.5L13 5" stroke="#C8A84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ─── PAGE ────────────────────────────────────────────────────────────────────

export function ClinicsPage() {
  const [searchParams] = useSearchParams()
  const [events, setEvents] = useState<MasterclassEvent[]>([])
  const [eventsLoading, setEventsLoading] = useState(true)
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [modalEvent, setModalEvent] = useState<MasterclassEvent | null>(null)
  const [modalTicketType, setModalTicketType] = useState<'rider' | 'auditor'>('rider')
  const [modalDay, setModalDay] = useState<string | undefined>(undefined)

  const showSuccess = searchParams.get('success') === 'true'

  // Fetch events from Firestore
  useEffect(() => {
    async function fetchEvents() {
      try {
        const q = query(
          collection(db, 'masterclassEvents'),
          where('active', '==', true),
          orderBy('startDate', 'asc'),
        )
        const snap = await getDocs(q)
        const docs: MasterclassEvent[] = snap.docs.map((d) => {
          const data = d.data()
          return {
            id: d.id,
            eventName: (data.eventName as string) ?? '',
            location: (data.location as string) ?? '',
            venueName: (data.venueName as string) ?? '',
            venueAddress: (data.venueAddress as string) ?? '',
            startDate: (data.startDate as Timestamp).toDate(),
            endDate: (data.endDate as Timestamp).toDate(),
            durationDays: data.durationDays === 2 ? 2 : data.durationDays === 3 ? 3 : undefined,
            riderTicketsSold: (data.riderTicketsSold as number) ?? 0,
            // Events created before capacity became editable fall back to 10.
            riderCapacity: (data.riderCapacity as number) || 10,
            riderSoldOut: (data.riderSoldOut as boolean) ?? false,
            active: (data.active as boolean) ?? true,
          }
        })
        setEvents(docs)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load events', err)
      } finally {
        setEventsLoading(false)
      }
    }
    fetchEvents()
  }, [])

  function openTicketModal(event: MasterclassEvent, ticketType: 'rider' | 'auditor', day?: string) {
    setModalEvent(event)
    setModalTicketType(ticketType)
    setModalDay(day)
    // Step 2 of 4: they asked to buy. Distinguishes "the page did not interest
    // anyone" from "the page worked and the form lost them".
    logGaEvent('select_item', {
      item_list_name: 'Masterclass',
      items: [{
        item_id: `masterclass-${ticketType}-${event.id}`,
        item_name: event.eventName,
        item_category: ticketType,
        item_variant: day ?? `${event.durationDays ?? 3}-day`,
        quantity: 1,
      }],
    })
  }

  function closeTicketModal() {
    setModalEvent(null)
    setModalDay(undefined)
  }

  function scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  // Auto-hide events whose endDate has passed by more than 24 hours.
  // Defensive: missing or invalid endDate defaults to showing the event.
  const visibleEvents = events.filter((e) => {
    if (!e.active) return false
    if (!e.endDate || isNaN(e.endDate.getTime())) return true
    return e.endDate.getTime() + 24 * 60 * 60 * 1000 >= Date.now()
  })

  return (
    <Layout>
      <ManagedSeo
        path="/masterclass"
        jsonLd={[
          AH_ORGANIZATION,
          SCOTT_PERSON,
          buildFaqSchema(FAQS),
          // visibleEvents, NOT events: the page already hides clinics whose
          // endDate has passed. Marking up an event a visitor cannot see is a
          // structured-data guidelines violation, and a past clinic advertised
          // as InStock is worse than no markup at all.
          ...buildClinicEventSchemas(visibleEvents),
        ]}
      />
      {/* ─── SUCCESS BANNER ──────────────────────────────────────────────── */}
      {showSuccess && (
        <div className="bg-green-700 py-3 px-4 text-center">
          <p className="font-body text-sm text-white font-semibold">
            You are registered. Check your email for confirmation.
          </p>
        </div>
      )}

      {/* ─── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative bg-navy overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${assets.clinics.groupRiders})` }}
        />
        <div className="absolute inset-0 bg-navy/85" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-3xl">
            <p className="font-body text-gold text-sm font-semibold tracking-widest uppercase mb-4">
              Riders Foundation Masterclass
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white leading-tight mb-6">
              You Have Done the Online Work. Now Come Do the Real Work.
            </h1>
            <p className="font-body text-lg text-white/80 leading-relaxed mb-10 max-w-2xl">
              The Riders Foundation Masterclass is direct in-person coaching with
              Scott Purdum. Available as 3-day and 2-day formats. You bring your
              horse. Scott builds a program around you. Ten riders maximum. When
              it fills, it closes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollTo('schedule')}
                style={{
                  backgroundColor: '#C8A84B',
                  color: '#0A1628',
                  fontWeight: '700',
                  padding: '16px 36px',
                  fontSize: '16px',
                  border: 'none',
                  cursor: 'pointer',
                  minHeight: '52px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                Get Tickets
              </button>
              <button
                onClick={() => scrollTo('breakdown')}
                className="font-body text-sm font-medium text-white hover:text-gold transition-colors inline-flex items-center justify-center min-h-[52px] px-6 border border-white/30 hover:border-gold/50"
              >
                See What to Expect
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PAIN ─────────────────────────────────────────────────────────── */}
      <section className="bg-navy py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-white text-center mb-12">
            You Already Know Something Is Missing.
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                text: 'You finish a ride knowing the same problems will be there next time. Nothing is actually changing.',
                icon: <RefreshCw size={32} color="#C8A84B" aria-hidden="true" />,
              },
              {
                text: 'You have had lessons. The feedback does not stick the way it needs to.',
                icon: <MessageSquareX size={32} color="#C8A84B" aria-hidden="true" />,
              },
              {
                text: 'You want someone who can see you and your horse together and tell you exactly what is happening between you.',
                icon: <Eye size={32} color="#C8A84B" aria-hidden="true" />,
              },
            ].map((point, i) => (
              <div key={i} className="border border-white/10 p-8 bg-white/5 flex flex-col gap-4">
                <div>{point.icon}</div>
                <p className="font-body text-base text-white/80 leading-relaxed">
                  {point.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MASTERCLASS FORMAT BREAKDOWN ─────────────────────────────────── */}
      <section id="breakdown" className="bg-white py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-4">
              Two Masterclass Formats
            </h2>
            <p className="font-body text-base text-navy/60">
              The format is set by the host venue and shown on each event below.
            </p>
          </div>

          {/* Format summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
            {/* 3-Day */}
            <div className="bg-white border border-navy/10 border-t-4 border-t-gold p-6">
              <p className="font-body text-sm uppercase tracking-widest text-gold font-semibold">
                3-Day Masterclass · $849
              </p>
              <p className="font-heading text-2xl text-navy mt-2">
                Friday + Saturday + Sunday
              </p>
              <p className="font-body text-sm text-navy/70 mt-2">
                Full experience including Friday's classroom lecture.
              </p>
            </div>

            {/* 2-Day */}
            <div className="bg-white border border-navy/10 border-t-4 border-t-gold p-6">
              <p className="font-body text-sm uppercase tracking-widest text-gold font-semibold">
                2-Day Masterclass · $599
              </p>
              <p className="font-heading text-2xl text-navy mt-2">
                Saturday + Sunday
              </p>
              <p className="font-body text-sm text-navy/70 mt-2">
                Weekend hands-on format.
              </p>
            </div>
          </div>

          {/* Unified day-by-day */}
          <div className="text-center mb-10">
            <h3 className="font-heading text-3xl text-navy">
              What Each Day Looks Like
            </h3>
          </div>

          <div className="space-y-10">
            {/* Friday */}
            <div className="border-l-4 border-gold pl-6">
              <h3 className="font-heading text-2xl text-navy mb-4">
                Friday – The Foundation Day
                <span className="font-body inline-block bg-gold text-navy text-xs uppercase tracking-wider px-2 py-0.5 font-semibold ml-3 align-middle">
                  3-Day Only
                </span>
              </h3>
              <div className="space-y-3">
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  <span className="font-semibold text-navy">Morning:</span> Classroom
                  lecture – "The Real Secret to Fixing Any Problem With Your Horse."
                  The framework that makes everything else make sense. Riders and
                  auditors together.
                </p>
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  <span className="font-semibold text-navy">Midday:</span> Lunch
                  together as a group.
                </p>
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  <span className="font-semibold text-navy">Afternoon:</span> Group
                  arena session. Apply the lecture directly with your horse.
                </p>
              </div>
            </div>

            {/* Saturday */}
            <div className="border-l-4 border-gold pl-6">
              <h3 className="font-heading text-2xl text-navy mb-4">
                Saturday – The Work Day
                <span className="font-body inline-block bg-navy/5 text-navy border border-navy/20 text-xs uppercase tracking-wider px-2 py-0.5 font-semibold ml-3 align-middle">
                  Both Formats
                </span>
              </h3>
              <div className="space-y-3">
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  <span className="font-semibold text-navy">Morning:</span> Group
                  session. Communication framework with your horse, in the arena.
                </p>
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  <span className="font-semibold text-navy">Midday:</span> Lunch
                  together as a group.
                </p>
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  <span className="font-semibold text-navy">Afternoon:</span> Semiprivate
                  breakout sessions. Scott creates a program specific to you and your
                  horse.
                </p>
              </div>
            </div>

            {/* Sunday */}
            <div className="border-l-4 border-gold pl-6">
              <h3 className="font-heading text-2xl text-navy mb-4">
                Sunday – The Integration Day
                <span className="font-body inline-block bg-navy/5 text-navy border border-navy/20 text-xs uppercase tracking-wider px-2 py-0.5 font-semibold ml-3 align-middle">
                  Both Formats
                </span>
              </h3>
              <div className="space-y-3">
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  <span className="font-semibold text-navy">Morning:</span> Semiprivate
                  continued. Lock in what you learned.
                </p>
                <p className="font-body text-sm text-navy/70 leading-relaxed">
                  <span className="font-semibold text-navy">Afternoon:</span> Group
                  session to close out. Pull together everything from the weekend
                  and head home with momentum.
                </p>
              </div>
            </div>
          </div>

          <p className="font-body text-xs text-navy/40 mt-8 text-center">
            Hours: Approximately 9:30am to 5pm each day. Times adjust per event.
          </p>

          {/* Auditor callout */}
          <div className="mt-10 bg-navy/5 border border-navy/10 p-6 sm:p-8 rounded-lg">
            <p className="font-body text-sm text-navy leading-relaxed">
              <span className="font-semibold text-gold">Auditors:</span> Friday is
              the day to prioritize. The morning lecture is classroom style, open to
              everyone, and covers the core framework Scott uses to diagnose every
              problem he sees. If you can only attend one day, attend Friday.
            </p>
          </div>

          {/* Groundwork photo */}
          <div className="mt-10 overflow-hidden rounded-lg">
            <img
              src={assets.clinics.indoorGroundwork}
              alt="Scott Purdum demonstrating groundwork techniques during a masterclass"
              width={1200}
              height={600}
              loading="lazy"
              className="w-full max-h-[280px] object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* ─── FOR RIDERS ───────────────────────────────────────────────────── */}
      <section className="bg-navy py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-white text-center mb-12">
            What You Are Getting as a Rider
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 mb-10">
            {[
              'Direct in-person coaching with Scott',
              'A program built specifically for you and your horse',
              'The Four Factors framework applied in real time',
              'Semiprivate sessions tailored to your specific challenges',
              'Maximum 10 riders per event',
              "Rider's Way App access included",
            ].map((item, i) => (
              <div key={i} className="flex gap-4 items-start">
                <CheckIcon />
                <p className="font-body text-base text-white/80 leading-relaxed">
                  {item}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => scrollTo('schedule')}
              style={{
                backgroundColor: '#C8A84B',
                color: '#0A1628',
                fontWeight: '700',
                padding: '16px 40px',
                fontSize: '16px',
                border: 'none',
                cursor: 'pointer',
                minHeight: '52px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              Get Rider Ticket
            </button>
          </div>
        </div>
      </section>

      {/* ─── FOR AUDITORS ─────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-navy text-center mb-4">
            What You Are Getting as an Auditor
          </h2>
          <p className="font-body text-base text-navy/60 text-center max-w-2xl mx-auto mb-12">
            You do not need a horse to learn from this event. Watching Scott work
            through real problems in real time changes how you see your own horse.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 mb-10">
            {[
              'Full access to every session in the event',
              'Watch Scott diagnose and work through real problems in real time',
              'Group Q&A access throughout the weekend',
              'Take notes, ask questions, bring your learning home',
            ].map((item, i) => (
              <div key={i} className="flex gap-4 items-start">
                <SmallCheckIcon />
                <p className="font-body text-sm text-navy/80 leading-relaxed">
                  {item}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mb-6">
            <p className="font-body text-3xl text-navy mb-2 font-bold">
              $20 per day
            </p>
            <p className="font-body text-sm text-navy/50 mb-6">
              Buy any day individually.
            </p>
            <button
              onClick={() => scrollTo('schedule')}
              className="inline-flex items-center justify-center font-body text-sm font-bold tracking-wider uppercase bg-navy text-white px-6 min-h-[52px] hover:bg-navy/90 transition"
            >
              See Auditor Pass Options
            </button>
          </div>

          <p className="font-body text-xs text-navy/40 text-center">
            Auditor passes are per day. Pick any day the event runs.
          </p>
          <div className="mt-6 bg-white border border-navy/10 p-5 rounded-lg max-w-xl mx-auto">
            <p className="font-body text-xs text-navy/60 leading-relaxed text-center">
              Friday is the recommended day for first-time auditors. The morning
              lecture sets the context for everything you will watch the rest of
              the weekend.
            </p>
          </div>
        </div>
      </section>

      {/* ─── CREDENTIALS ──────────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
            <div className="mb-10 lg:mb-0">
              <img
                src={assets.scott.coachingStirrup}
                alt="Scott Purdum coaching a rider during a masterclass"
                width={600}
                height={500}
                loading="lazy"
                className="w-full h-80 lg:h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-10">
                Why Riders Travel to Work With Scott
              </h2>
              <div className="space-y-8">
                {CREDENTIALS.map((c) => (
                  <div key={c.stat}>
                    <p className="font-body text-3xl text-gold mb-1 font-bold">
                      {c.stat}
                    </p>
                    <p className="font-body text-sm text-navy/70 leading-relaxed">
                      {c.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SCHEDULE ─────────────────────────────────────────────────────── */}
      <section id="schedule" className="py-16 lg:py-24" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-navy text-center mb-12">
            Upcoming Masterclasses
          </h2>

          {eventsLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : visibleEvents.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-body text-base text-navy/60">
                New dates coming soon. Join the email list below to be notified.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {visibleEvents.map((event) => {
                const durationDays = event.durationDays ?? 3
                const dayLabels = getEventDayLabels(event.startDate, durationDays)
                const riderPrice = durationDays === 2 ? '$599' : '$849'
                const riderDescriptor = `Full ${durationDays}-day participation with your horse · ${riderPrice}`
                const auditorGridCols = durationDays === 2 ? 'grid-cols-2' : 'grid-cols-3'
                return (
                <div key={event.id} className="bg-white p-5 sm:p-7 border border-navy/10">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div>
                      <span className="inline-block font-body text-[10px] font-bold tracking-wider uppercase px-2 py-1 border border-navy/30 text-navy/70 mb-2">
                        {durationDays}-Day
                      </span>
                      <h3 className="font-heading text-2xl text-navy mb-1">
                        {event.eventName}
                      </h3>
                      <p className="font-body text-sm text-navy/60 mb-1">
                        {event.location}
                        {event.venueName ? ` · ${event.venueName}` : ''}
                      </p>
                      {event.venueAddress && (
                        <p className="font-body text-xs text-navy/40 mb-1">
                          {event.venueAddress}
                        </p>
                      )}
                      <p className="font-body text-sm font-semibold text-gold">
                        {formatDateRange(event.startDate, event.endDate)}
                      </p>
                      <p className="font-body text-xs text-navy/40 mt-1">
                        Approximately 9:30am – 5:00pm daily. Times may vary.
                      </p>
                    </div>

                    <div className="lg:w-72 flex flex-col">
                      {/* RIDER SECTION */}
                      <div>
                        <h4 className="text-sm tracking-widest uppercase text-navy font-semibold mb-2">
                          Rider Registration
                        </h4>
                        <p className="text-sm text-navy/70 mb-3">
                          {riderDescriptor}
                        </p>
                        {event.riderSoldOut ? (
                          <button
                            disabled
                            className="w-full font-body text-sm font-semibold border-2 border-gold/50 text-navy/40 px-6 py-2 min-h-[44px] cursor-not-allowed"
                          >
                            Sold Out
                          </button>
                        ) : (
                          <button
                            onClick={() => openTicketModal(event, 'rider')}
                            style={{
                              width: '100%',
                              backgroundColor: '#C8A84B',
                              color: '#0A1628',
                              fontWeight: '700',
                              padding: '10px 28px',
                              fontSize: '14px',
                              border: 'none',
                              cursor: 'pointer',
                              minHeight: '44px',
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Get Rider Ticket
                          </button>
                        )}
                      </div>

                      {/* DIVIDER */}
                      <div className="border-t border-gold/40 my-5"></div>

                      {/* AUDITOR SECTION */}
                      <div>
                        <h4 className="text-sm tracking-widest uppercase text-navy font-semibold mb-2">
                          Auditor Day Passes
                        </h4>
                        <p className="text-sm text-navy/70 mb-3">
                          Observe Scott teaching live. No horse needed.
                        </p>
                        <div className={`grid ${auditorGridCols} gap-2`}>
                          {dayLabels.map((day) => (
                            <button
                              key={day}
                              onClick={() => openTicketModal(event, 'auditor', day)}
                              className="font-body text-xs font-semibold border border-navy/20 text-navy px-2 py-2 min-h-[40px] hover:bg-navy hover:text-white transition-colors"
                            >
                              {day} $20
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* ─── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-lg mb-12">
            <img
              src={assets.clinics.indoorRiding}
              alt="Riders practicing in the arena during a masterclass with Scott Purdum"
              width={1200}
              height={500}
              loading="lazy"
              className="w-full max-h-[280px] object-cover object-center"
            />
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl text-navy text-center mb-12">
            From Riders Who Have Been There
          </h2>
          <div className="grid lg:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="border border-gray-100 p-8">
                <div className="font-heading text-5xl text-gold/30 leading-none mb-4 select-none">
                  &ldquo;
                </div>
                <blockquote className="font-body text-base text-navy/80 leading-relaxed mb-6">
                  {t.quote}
                </blockquote>
                <p className="font-body text-sm font-semibold text-navy">
                  {t.name}
                </p>
                <p className="font-body text-xs text-navy/50">{t.location}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── INVESTMENT SUMMARY ───────────────────────────────────────────── */}
      <section className="bg-navy py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl text-white mb-8">
            Investment
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 mb-10">
            <div className="border border-white/10 p-6 bg-white/5">
              <p className="font-body text-4xl text-white mb-2 font-bold">$849</p>
              <p className="font-body text-sm text-white/60 mb-2">Rider Ticket</p>
              <p className="font-body text-xs text-white/40">Payment plan available at checkout</p>
            </div>
            <div className="border border-white/10 p-6 bg-white/5">
              <p className="font-body text-4xl text-white mb-2 font-bold">$20</p>
              <p className="font-body text-sm text-white/60 mb-2">Auditor Day Pass</p>
              <p className="font-body text-xs text-white/40">Friday, Saturday, Sunday available separately</p>
            </div>
          </div>
          <div className="border border-white/10 p-6 max-w-xl mx-auto">
            <p className="font-body text-xs font-semibold tracking-widest uppercase text-white/40 mb-3">
              Refund Policy
            </p>
            <p className="font-body text-xs text-white/50 leading-relaxed">
              Cancellations more than two weeks before the event start date
              receive a refund minus a $200 non-refundable deposit. Cancellations
              within two weeks are not eligible for a refund. Email
              allie@advantagehorsemanship.com to request a cancellation.
            </p>
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-24" style={{ borderTop: '1px solid #f0f0f0' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-navy text-center mb-12">
            Common Questions
          </h2>
          <div className="divide-y divide-navy/10">
            {FAQS.map((faq, i) => (
              <div key={i}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left py-5 flex items-start justify-between gap-4 min-h-[52px]"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-body text-base font-semibold text-navy">
                    {faq.q}
                  </span>
                  <span
                    className={`flex-shrink-0 text-gold mt-0.5 transition-transform duration-200 ${
                      openFaq === i ? 'rotate-45' : ''
                    }`}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="pb-5">
                    <p className="font-body text-sm text-navy/70 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#C8A84B' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-5xl sm:text-6xl text-navy mb-4">
            Ten Riders. Three Days. Real Work.
          </h2>
          <p className="font-body text-navy/70 text-lg mb-8">
            When it fills, it closes. Get your tickets now.
          </p>
          <button
            onClick={() => scrollTo('schedule')}
            style={{
              backgroundColor: '#0A1628',
              color: '#FFFFFF',
              fontWeight: '700',
              padding: '18px 48px',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              minHeight: '52px',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Get Tickets
          </button>
          <p className="font-body text-xs text-navy/50 mt-4">
            Questions?{' '}
            <Link
              to="/contact"
              className="underline hover:text-navy transition-colors"
            >
              Contact us
            </Link>
          </p>
        </div>
      </section>

      {modalEvent && (
        <MasterclassTicketModal
          isOpen={modalEvent !== null}
          onClose={closeTicketModal}
          eventId={modalEvent.id}
          eventName={modalEvent.eventName}
          eventStartDate={modalEvent.startDate}
          ticketType={modalTicketType}
          day={modalDay}
          durationDays={modalEvent.durationDays ?? 3}
          riderSeatsRemaining={Math.max(
            0,
            modalEvent.riderCapacity - modalEvent.riderTicketsSold,
          )}
        />
      )}
    </Layout>
  )
}
